import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Link,
  useTheme
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { allAnimals } from '../data/animalData';
import { getFilteredAnimals, getProductInfoForAnimal, getFoodInfoForAnimal, RARITY_ORDER, formatProductionTime, animalTypes } from '../utils/animalUtils';
import { tableStyles as themeTableStyles } from '../theme/tableStyles';
import { tabStyles, avatarStyles, mainCellGap, tabContainerStyles } from '../theme/componentStyles';
import { typography, cellStyles, flex } from '../theme/typography';
import RarityBadge from '../components/common/RarityBadge';
import RarityAvatar from '../components/common/RarityAvatar';
import AnimalUpgradePath from '../components/AnimalUpgradePath/AnimalUpgradePath';
import UpgradeRequirements from '../components/UpgradeRequirements';

function AnimalRow({ animal, tableStyles, mainCellGap, currentAnimalType, getProductInfoForAnimal, getFoodInfoForAnimal, avatarStyles, RouterLink, Tooltip, RarityAvatar, RarityBadge, formatProductionTime, Link }) {
  const rowRef = React.useRef(null);
  const cellRefs = React.useRef([]);
  if (cellRefs.current.length !== 7) {
    cellRefs.current = Array(7).fill().map(() => React.createRef());
  }

  React.useEffect(() => {
    // Remove all layout measurement and debug code from Animals.jsx
  }, [animal]);

  return (
    <TableRow ref={rowRef} sx={{ '&:hover': { bgcolor: 'transparent' } }}>
      <TableCell 
        ref={cellRefs.current[0]} 
        sx={{ 
          ...tableStyles.tableCell, 
          ...cellStyles.base,
          width: '30%',
          height: 80,
          minHeight: 80,
          maxHeight: 80,
          p: 1,
          textAlign: 'center'
        }}
      >
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          textAlign: 'center'
        }}>
          <Tooltip title={`${animal.name} (${animal.rarityType})`} arrow>
            <Box>
              <RarityAvatar
                src={animal.image}
                alt={`${animal.name} (${animal.rarityType})`}
                size="md"
                sx={{ 
                  margin: '0 auto',
                  display: 'block',
                  width: '40px !important',
                  height: '40px !important',
                  '& img': {
                    width: '100% !important',
                    height: '100% !important',
                    objectFit: 'contain !important',
                    objectPosition: 'center',
                    p: 0,
                    margin: 'auto',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                  }
                }}
                aria-label={`${animal.name} (${animal.rarityType})`}
              />
            </Box>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell 
        ref={cellRefs.current[1]} 
        sx={{ 
          ...tableStyles.tableCell, 
          ...cellStyles.base,
          ...cellStyles.center,
          width: '20%',
          height: 80,
          minHeight: 80,
          maxHeight: 80,
          p: 2
        }}
      >
        <Box sx={{ ...flex.center, height: 40 }}>
          <RarityBadge rarity={animal.rarityType} size="small" />
        </Box>
      </TableCell>
      <TableCell 
        ref={cellRefs.current[2]} 
        sx={{ 
          ...tableStyles.tableCell, 
          ...cellStyles.base,
          ...cellStyles.center,
          width: '15%',
          height: 80,
          minHeight: 80,
          maxHeight: 80,
          p: 2
        }}
      >
        <Box sx={{ ...flex.center, height: 40 }}>
          <Typography sx={{ ...typography.body, fontWeight: 600, color: 'text.primary' }}>
            {formatProductionTime(animal.productionTime)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell 
        ref={cellRefs.current[3]} 
        sx={{ 
          ...tableStyles.tableCell, 
          width: '15%', 
          textAlign: 'center', 
          height: 60, 
          minHeight: 60, 
          maxHeight: 60,
          p: 2,
          borderRight: 'none !important'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Tooltip title={`View ${getProductInfoForAnimal(animal)?.id || ''} products`} arrow>
            <Link
              component={RouterLink}
              to="/products"
              state={{ tab: getProductInfoForAnimal(animal)?.tabId || 'eggs' }}
              sx={{ textDecoration: 'none' }}
            >
              <RarityAvatar
                src={getProductInfoForAnimal(animal)?.image.startsWith('/images/') 
                  ? getProductInfoForAnimal(animal)?.image 
                  : `/images/vegetables/${getProductInfoForAnimal(animal)?.image || 'egg'}/common/preview.png`}
                alt={`${getProductInfoForAnimal(animal)?.id || ''} product`}
                size="md"
                sx={avatarStyles.standard}
                aria-label={`View ${getProductInfoForAnimal(animal)?.id || ''} products`}
              />
            </Link>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell 
        ref={cellRefs.current[4]} 
        sx={{ 
          ...tableStyles.tableCell, 
          ...cellStyles.base,
          ...cellStyles.center,
          width: '10%',
          height: 80,
          minHeight: 80,
          maxHeight: 80,
          p: 2,
          borderLeft: 'none !important'
        }}
      >
        <Box sx={{ ...flex.center, height: 40 }}>
          <Typography sx={{ ...typography.number, fontWeight: 600, color: 'text.primary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {(() => {
              switch (animal.rarityType) {
                case 'Common': return '× 1';
                case 'Uncommon': return '× 2';
                case 'Rare': return '× 4';
                case 'Epic': return '× 8';
                case 'Legendary': return '× 16';
                default: return '';
              }
            })()}
          </Typography>
        </Box>
      </TableCell>
      <TableCell 
        ref={cellRefs.current[5]} 
        sx={{ 
          ...tableStyles.tableCell, 
          width: '10%', 
          textAlign: 'center', 
          height: 60, 
          minHeight: 60, 
          maxHeight: 60,
          p: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Tooltip title={`View ${getFoodInfoForAnimal(animal)?.id || ''} food`} arrow>
            <Link
              component={RouterLink}
              to={getFoodInfoForAnimal(animal)?.foodPath || '/food'}
              sx={{ textDecoration: 'none' }}
            >
              <RarityAvatar
                src={getFoodInfoForAnimal(animal)?.image?.startsWith('/images/') 
                  ? getFoodInfoForAnimal(animal)?.image 
                  : currentAnimalType === 'birds' 
                    ? '/images/seeds/egg/common/preview.png' 
                    : getFoodInfoForAnimal(animal)?.image 
                      ? `/images/seeds/${getFoodInfoForAnimal(animal).image}/common/preview.png` 
                      : ''}
                alt={`View ${getFoodInfoForAnimal(animal)?.id || ''} food`}
                size="md"
                sx={avatarStyles.standard}
                aria-label={`View ${getFoodInfoForAnimal(animal)?.id || ''} food`}
              />
            </Link>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell 
        ref={cellRefs.current[6]}
        sx={{ 
          ...tableStyles.tableCell, 
          ...cellStyles.base,
          ...cellStyles.center,
          width: '15%',
          p: 0.5,
          verticalAlign: 'middle',
          borderLeft: '1px solid',
          borderLeftColor: 'divider',
          '&:last-child': {
            borderRight: 'none',
          },
        }}
      >
        {animal.rarity[0].upgradeRequirements && (
          <UpgradeRequirements 
            requirements={{
              ...animal.rarity[0].upgradeRequirements,
              rarity: animal.rarity[0].type
            }}
            currentAnimalType={currentAnimalType}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

const Animals = () => {
  const theme = useTheme();
  const tableStyles = useMemo(() => {
    const styles = themeTableStyles(theme);
    return {
      ...styles,
      tableContainer: {
        ...styles.tableContainer,
        '&:hover': {
          transform: 'none !important',
          boxShadow: 'none !important'
        }
      },
      tableRow: {
        '&:hover': {
          backgroundColor: 'transparent !important'
        }
      }
    };
  }, [theme]);
  const [tabValue, setTabValue] = useState(() => {
    const hash = window.location.hash.substring(1);
    const tabIndex = animalTypes.findIndex(type => type.id === hash);
    return tabIndex >= 0 ? tabIndex : 0;
  });

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      const tabIndex = animalTypes.findIndex(type => type.id === hash);
      if (tabIndex >= 0 && tabIndex !== tabValue) {
        setTabValue(tabIndex);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [tabValue]);

  const currentAnimalType = animalTypes[tabValue]?.id;
  console.log('Current animal type:', currentAnimalType);
  const filteredAnimals = useMemo(() => {
    const animals = getFilteredAnimals(allAnimals[currentAnimalType] || []);
    return [...animals].sort((a, b) => RARITY_ORDER[a.rarityType] - RARITY_ORDER[b.rarityType]);
  }, [currentAnimalType]);

  return (
    <Box sx={{ 
      ...tabContainerStyles.outer, 
      width: '100%',
      minHeight: '100vh',
      pt: 4,
      pb: 8, // Increased bottom padding to prevent footer cutoff
      overflowX: 'auto',
      '&::-webkit-scrollbar': {
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0.1)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
      },
    }}>
      <Container maxWidth={false} disableGutters sx={{ ...tabContainerStyles.inner, width: 1200, mx: 'auto' }}>
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: 0 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => {
              setTabValue(newValue);
              const animalType = animalTypes[newValue]?.id;
              if (animalType) {
                // Update the URL hash without causing a page reload
                window.history.pushState(null, '', `#${animalType}`);
                // Force a re-render by dispatching a hashchange event
                window.dispatchEvent(new HashChangeEvent('hashchange'));
              }
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              ...tabStyles.container,
              width: '100%',
              '& .MuiTabs-flexContainer': {
                display: 'flex',
                justifyContent: 'space-around',
                width: '100%',
                gap: 2
              },
              '& .MuiTab-root': {
                minWidth: 'auto',
                flex: 1,
                maxWidth: 'none',
                padding: '6px 8px'
              }
            }}
            aria-label="animal database"
          >
            {animalTypes.map((type, index) => (
              <Tab
                key={type.id}
                label={
                  <Box sx={tabStyles.content}>
                    <RarityAvatar
                      src={type.image}
                      alt={type.name}
                      size="md"
                      sx={avatarStyles.standard}
                      aria-label={`View ${type.name} animals`}
                    />
                    <Typography variant="caption" sx={tabStyles.label}>
                      {type.name}
                    </Typography>
                  </Box>
                }
                sx={{ width: '400px', ...tabStyles.root }}
              />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: 0 }}>
          <TableContainer sx={{ 
            ...tableStyles.tableContainer, 
            width: '100%',
            maxWidth: 1200,
            mx: 'auto',
            overflow: 'hidden',
            boxShadow: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            background: 'transparent',
            '& .MuiTableCell-root': {
              textAlign: 'center',
              verticalAlign: 'middle',
              lineHeight: 1,
              height: 40,
              minHeight: 40,
              maxHeight: 40,
              padding: '4px 16px'
            },
            '&:hover': {
              boxShadow: 4
            }
          }}>
            <Table size="small" aria-label="animal database">
              <TableHead sx={tableStyles.tableHead}>
                <TableRow>
                  <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '25%', ...cellStyles.center }}>Animal</TableCell>
                  <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '15%', ...cellStyles.center }}>Rarity</TableCell>
                  <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '12%', ...cellStyles.center }}>Production Time</TableCell>
                  <TableCell colSpan={2} sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '23%', ...cellStyles.center }}>Product</TableCell>
                  <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '10%', ...cellStyles.center }}>Food</TableCell>
                  <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '15%', ...cellStyles.center }}>Upgrade Requirements</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={tableStyles.tableBody}>
                {filteredAnimals.filter(Boolean).map((animal) => (
                  <AnimalRow
                    key={animal.id}
                    animal={animal}
                    tableStyles={tableStyles}
                    mainCellGap={mainCellGap}
                    currentAnimalType={currentAnimalType}
                    getProductInfoForAnimal={getProductInfoForAnimal}
                    getFoodInfoForAnimal={getFoodInfoForAnimal}
                    avatarStyles={avatarStyles}
                    RouterLink={RouterLink}
                    Tooltip={Tooltip}
                    RarityAvatar={RarityAvatar}
                    RarityBadge={RarityBadge}
                    formatProductionTime={formatProductionTime}
                    Link={Link}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        
        {/* Animal Upgrade Path Section */}
        <AnimalUpgradePath animalType={currentAnimalType} />
        
      </Container>
    </Box>
  );
};

export default Animals;
